import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 30000 });

export const startGrade = (property) =>
  http.post("/grade/start", { property }).then((r) => r.data);

export const getStatus = (analysisId) =>
  http.get(`/grade/status/${analysisId}`).then((r) => r.data);

export const getTier1 = (analysisId) =>
  http.get(`/grade/tier1/${analysisId}`).then((r) => r.data);

export const unlockReport = (payload) =>
  http.post("/grade/unlock", payload).then((r) => r.data);

export const accessByToken = (token) =>
  http.post("/grade/access", { token }).then((r) => r.data);

export const getTier2 = (analysisId) =>
  http.get(`/grade/tier2/${analysisId}`).then((r) => r.data);

export const adminListAnalyses = (token) =>
  http.get("/admin/analyses", { headers: { "X-Admin-Token": token } }).then((r) => r.data);

export const adminGetAnalysis = (analysisId, token) =>
  http
    .get(`/admin/analysis/${analysisId}`, { headers: { "X-Admin-Token": token } })
    .then((r) => r.data);

export const adminStats = (token) =>
  http.get("/admin/stats", { headers: { "X-Admin-Token": token } }).then((r) => r.data);

export const adminListLeads = (token) =>
  http.get("/admin/leads", { headers: { "X-Admin-Token": token } }).then((r) => r.data);

export const adminHealth = (token, { force = false } = {}) =>
  http
    .get(`/admin/health${force ? "?force=true" : ""}`, { headers: { "X-Admin-Token": token } })
    .then((r) => r.data);

// ------------ Places autocomplete ------------
export const placesAutocomplete = (q, { signal } = {}) =>
  http.get(`/places/autocomplete`, { params: { q }, signal }).then((r) => r.data);

export const placesDetails = (place_id) =>
  http.get(`/places/details`, { params: { place_id } }).then((r) => r.data);

// ------------ Score-Lift Dashboard (R3-backed, hotel-facing) ------------
export const getLiftDashboard = (analysisId) =>
  http.get(`/grade/tier2/${analysisId}/lift-dashboard`).then((r) => r.data);

// ------------ FieldGuide handoff ------------
// The handoff pipeline (compose + FG intake POST) regularly runs 30–90s.
// We refactored it to enqueue + poll on 2026-07-31 so the browser never
// holds a >5s HTTP connection and can't trip CloudFlare's ~100s timeout.
export const startFieldGuideHandoff = (analysisId) =>
  http.post("/handoff/fieldguide", { analysis_id: analysisId }, { timeout: 15000 }).then((r) => r.data);

export const getFieldGuideHandoffStatus = (handoffId) =>
  http.get(`/handoff/status/${handoffId}`, { timeout: 15000 }).then((r) => r.data);

/**
 * Poll a handoff job to completion. Resolves with the terminal payload
 * (same shape as the pre-async sync response for success; error shape for
 * failure). Never rejects for timeout — instead resolves with a
 * poll-timeout error object so callers use one consistent UX path.
 */
export async function handoffToFieldGuide(analysisId, {
  intervalMs = 2000,
  maxWaitMs = 180000,  // 3 min ceiling — well beyond typical 30–60s
} = {}) {
  const start = await startFieldGuideHandoff(analysisId);
  const { handoff_id } = start;
  const deadline = Date.now() + maxWaitMs;
  // Small initial delay — the pipeline never returns in <5s so polling
  // immediately just wastes a request.
  await new Promise((r) => setTimeout(r, 3000));
  while (Date.now() < deadline) {
    const s = await getFieldGuideHandoffStatus(handoff_id);
    if (s.status === "complete" || s.status === "failed") return s;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return {
    handoff_id,
    status: "failed",
    ok: false,
    error_code: "handoff_poll_timeout",
    error_message: "Your FieldGuide is taking longer than expected. Please try again in a moment.",
  };
}
